/* KN mobile menu: isolated from Dawn's desktop navigation and drawer controller. */
(() => {
  if (customElements.get('kn-mobile-menu')) return;

  class KnMobileMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.kn-mobile-menu__dialog');
      this.trigger = document.getElementById(this.dataset.trigger);
      if (!this.dialog || !this.trigger) return;
      this.root = this.dialog.querySelector('[data-kn-root]');
      this.back = this.dialog.querySelector('[data-kn-back]');
      this.logo = this.dialog.querySelector('.kn-mobile-menu__logo');
      this.closeButton = this.dialog.querySelector('[data-kn-close]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.dataset.fallback === 'true') this.sanitizeFallbackTitles();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      // KN mobile menu: Safari can retain :focus-visible across scripted touch focus.
      this.setInputMode('keyboard');
      ['pointerdown', 'touchstart', 'mousedown'].forEach((type) => {
        document.addEventListener(type, () => this.setInputMode('pointer'), {
          ...options, capture: true, passive: true,
        });
      });
      document.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || ['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
        this.setInputMode('keyboard');
      }, { ...options, capture: true });
      // A body portal avoids the sticky header's transforms and app stacking contexts.
      document.body.appendChild(this.dialog);
      this.trigger.addEventListener('click', () => this.open(), options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.back.addEventListener('click', () => this.showRoot(), options);
      this.dialog.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kn-sub]');
        if (button) this.showSub(button);
        // Localization links submit Dawn's existing form rather than navigate directly.
        const link = event.target.closest('a[href]');
        if (link && !link.closest('localization-form')) this.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.trapTab(event), options);
      this.dialog.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        this.activePanel ? this.showRoot() : this.close();
      }, options);
      this.dialog.addEventListener('touchstart', (event) => {
        this.touch = { x: event.touches[0]?.clientX, y: event.touches[0]?.clientY };
      }, { ...options, passive: true });
      this.dialog.addEventListener('touchmove', (event) => this.preventBounce(event), { ...options, passive: false });
      this.mobile.addEventListener('change', () => {
        this.trigger.hidden = !this.mobile.matches;
        if (!this.mobile.matches) this.close({ immediate: true, restoreFocus: false });
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        if (this.mobile.matches && this.dialog.contains(event.target)) this.open();
      }, options);
      this.trigger.hidden = !this.mobile.matches;
    }

    setInputMode(mode) {
      this.trigger.setAttribute('data-kn-input-mode', mode);
      this.dialog.setAttribute('data-kn-input-mode', mode);
    }

    sanitizeFallbackTitles() {
      const clean = (value) => {
        let text = value.replace(/[#*0-9]\uFE0F?\u20E3/g, ' ')
          .replace(/[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0E\uFE0F\u200D\u20E3]/gu, ' ');
        let previous;
        do {
          previous = text;
          text = text.replace(/\([^()]*\)|\[[^\[\]]*\]|\{[^{}]*\}/g, ' ');
        } while (text !== previous);
        return text.replace(/\s+/g, ' ').trim();
      };
      // Only the Header Menu fallback is normalized; selected mobile menu copy is preserved.
      this.dialog.querySelectorAll('.kn-mobile-menu__title').forEach((label) => {
        label.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) node.nodeValue = clean(node.nodeValue);
        });
        const suffix = label.querySelector('small');
        if (suffix) {
          suffix.textContent = clean(suffix.textContent);
          if (!suffix.textContent) suffix.remove();
        }
      });
      this.dialog.querySelectorAll('[data-kn-panel]').forEach((panel) => {
        const lead = panel.querySelector('.kn-mobile-menu__lead');
        lead.textContent = clean(lead.textContent);
        panel.setAttribute('aria-label', lead.textContent);
        const parent = lead.textContent.split(' · ')[0].trim();
        const word = parent.split(' ').at(-1);
        const all = panel.querySelector('.kn-mobile-menu__all');
        if (!all) return;
        const label = `Shop All ${word}`;
        if (!word || word === parent || label.toLowerCase() === parent.toLowerCase()) all.remove();
        else all.textContent = `${label} →`;
      });
    }

    disconnectedCallback() {
      this.close({ immediate: true, restoreFocus: false });
      this.listeners?.abort();
      this.dialog?.remove();
    }

    open() {
      if (this.isOpen || !this.mobile.matches || !this.isConnected) return;
      clearTimeout(this.closeTimer);
      this.isOpen = true;
      this.dialog.classList.remove('is-closing');
      this.resetPanels();
      this.root.scrollTop = 0;
      this.lockScroll();
      this.dialog.hidden = false;
      this.dialog.inert = false;
      this.dialog.removeAttribute('aria-hidden');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.isolateBackground();
      if (!this.imagesLoaded) {
        this.dialog.querySelectorAll('img[data-src]').forEach((img) => {
          img.addEventListener('error', () => { img.style.display = 'none'; }, { once: true });
          img.src = img.dataset.src;
          delete img.dataset.src;
        });
        this.imagesLoaded = true;
      }
      // Flush the closed state so the stagger restarts on every opening.
      void this.dialog.offsetWidth;
      this.dialog.classList.add('is-open');
      this.closeButton.focus({ preventScroll: true });
      this.focusListeners = new AbortController();
      document.addEventListener('focusin', (event) => {
        if (this.isOpen && !this.dialog.contains(event.target)) this.closeButton.focus({ preventScroll: true });
      }, { signal: this.focusListeners.signal });
    }

    close({ immediate = false, restoreFocus = true } = {}) {
      if (!this.dialog || !this.root) return;
      clearTimeout(this.closeTimer);
      this.focusListeners?.abort();
      if (this.isOpen) this.dialog.classList.add('is-closing');
      this.dialog.classList.remove('is-open');
      this.dialog.inert = true;
      this.dialog.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.isOpen) {
        this.dialog.querySelectorAll('localization-form').forEach((form) => {
          if (form.elements?.button?.getAttribute('aria-expanded') === 'true') form.hidePanel?.();
        });
        this.isOpen = false;
        this.restoreBackground();
        this.unlockScroll();
        if (restoreFocus && this.trigger.isConnected && this.mobile.matches) this.trigger.focus({ preventScroll: true });
      }
      const finish = () => {
        this.dialog.hidden = true;
        this.dialog.classList.remove('is-closing');
        this.resetPanels();
      };
      if (immediate || this.motion?.matches) finish();
      else this.closeTimer = setTimeout(finish, 220);
    }

    resetPanels() {
      this.dialog.classList.remove('in-sub');
      this.dialog.querySelectorAll('[data-kn-panel]').forEach((panel) => {
        panel.classList.remove('is-active');
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
      });
      this.dialog.querySelectorAll('[data-kn-sub]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.root.inert = false;
      this.root.removeAttribute('aria-hidden');
      this.logo.inert = false;
      this.back.inert = true;
      this.activePanel = null;
      this.subTrigger = null;
    }

    showSub(button) {
      const panel = document.getElementById(button.dataset.knSub);
      if (!this.isOpen || !panel || !this.dialog.contains(panel)) return;
      panel.scrollTop = 0;
      panel.inert = false;
      panel.removeAttribute('aria-hidden');
      panel.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');
      this.dialog.classList.add('in-sub');
      this.back.inert = false;
      this.root.inert = true;
      this.root.setAttribute('aria-hidden', 'true');
      this.logo.inert = true;
      this.activePanel = panel;
      this.subTrigger = button;
      panel.focus({ preventScroll: true });
    }

    showRoot() {
      if (!this.activePanel) return;
      const trigger = this.subTrigger;
      this.resetPanels();
      trigger?.focus({ preventScroll: true });
    }

    trapTab(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const elements = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => !el.closest('[inert], [hidden]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = elements[0], last = elements.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.activePanel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.scroll = { x: window.scrollX, y: window.scrollY };
      const body = document.body;
      this.bodyStyles = {};
      ['position', 'top', 'left', 'right', 'width', 'padding-right'].forEach((key) => {
        this.bodyStyles[key] = { value: body.style.getPropertyValue(key), priority: body.style.getPropertyPriority(key) };
      });
      const gutter = window.innerWidth - document.documentElement.clientWidth;
      if (gutter) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + gutter}px`;
      body.style.position = 'fixed';
      body.style.top = `${-this.scroll.y}px`;
      body.style.left = `${-this.scroll.x}px`;
      body.style.right = '0';
      body.style.width = '100%';
      document.documentElement.classList.add('kn-mobile-menu-open');
    }

    unlockScroll() {
      document.documentElement.classList.remove('kn-mobile-menu-open');
      Object.entries(this.bodyStyles || {}).forEach(([key, { value, priority }]) => {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      });
      // Override smooth page scrolling only for the exact saved-position restore.
      const html = document.documentElement;
      const value = html.style.getPropertyValue('scroll-behavior');
      const priority = html.style.getPropertyPriority('scroll-behavior');
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      if (this.scroll) window.scrollTo(this.scroll.x, this.scroll.y);
      if (value) html.style.setProperty('scroll-behavior', value, priority);
      else html.style.removeProperty('scroll-behavior');
    }

    isolateBackground() {
      this.background = new Map();
      const isolate = (element) => {
        if (element === this.dialog || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) return;
        this.background.set(element, element.inert);
        element.inert = true;
      };
      [...document.body.children].forEach(isolate);
      this.backgroundObserver = new MutationObserver((records) => records.forEach((record) => {
        record.addedNodes.forEach((node) => { if (node.nodeType === Node.ELEMENT_NODE) isolate(node); });
      }));
      this.backgroundObserver.observe(document.body, { childList: true });
    }

    restoreBackground() {
      this.backgroundObserver?.disconnect();
      this.background?.forEach((wasInert, element) => { element.inert = wasInert; });
      this.background?.clear();
    }

    preventBounce(event) {
      if (!this.touch || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - this.touch.x, dy = touch.clientY - this.touch.y;
      this.touch = { x: touch.clientX, y: touch.clientY };
      const strip = event.target.closest('.kn-mobile-menu__strip');
      if (strip && Math.abs(dx) > Math.abs(dy) && strip.scrollWidth > strip.clientWidth) return;
      const panel = event.target.closest('.kn-mobile-menu__panel');
      const selector = event.target.closest('.country-selector__list, .country-selector, .disclosure__list');
      const scroller = selector && selector.scrollHeight > selector.clientHeight ? selector : panel;
      if (!scroller || scroller.scrollHeight <= scroller.clientHeight || (dy > 0 && scroller.scrollTop <= 0) || (dy < 0 && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1)) event.preventDefault();
    }
  }

  customElements.define('kn-mobile-menu', KnMobileMenu);
  // Custom elements reconnect automatically after shopify:section:load replacements.
})();
